import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatMoney, isRetryableApiError } from '@barahpay/shared';
import { checkoutApi } from '../api';
import { CheckoutCard } from '../components/CheckoutCard';
import { PaymentProgress } from '../components/PaymentProgress';

/** A payment still open after this long is not going to settle while the payer watches. */
const POLL_CEILING_MS = 120_000;

/** Past this, the wait is worth explaining rather than leaving as a bare spinner. */
const SLOW_AFTER_MS = 20_000;

/** Statuses that mean the provider has not finished with the payment yet. */
function isOpen(status: string | undefined): boolean {
  return status === 'Pending' || status === 'Processing' || status === 'Created';
}

/**
 * The public result page (spec section 77). The redirect target comes from the server, which validated
 * it against the product's registered URLs — a redirect parameter from the browser is never trusted
 * (spec section 78).
 */
export function ResultPage() {
  const { token = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language.startsWith('ar');

  /*
   * What the provider put on the URL when it sent the payer back here.
   *
   * Neither the page nor the server uses its value — it came through the payer's browser, and nothing
   * from there may choose what gets looked up. What matters is that it exists only on the arrival
   * from the provider: the first load is the moment the answer became available, and it is worth
   * asking the gateway for even if a cached one is sitting there.
   *
   * Captured in a ref on first render so it survives the language toggle rewriting the URL.
   */
  const returnReference = useRef(searchParams.get('resourcePath') ?? undefined);

  /*
   * Wall-clock since the payer landed here, ticking once a second while the payment is open.
   *
   * It drives two things the page previously had no way to express: the "this is taking longer than
   * usual" line, and a ceiling on the polling. Without the ceiling an unsettled payment polled the
   * result endpoint every three seconds for as long as the tab stayed open — and that endpoint asks
   * the provider, which rate-limits status calls per checkout, so a forgotten tab was a slow way to
   * exhaust a real quota.
   */
  // Mirrored in a ref as well as state: the render reads the state, while refetchInterval runs outside
  // render and needs the current value without re-subscribing the query each second.
  const elapsedRef = useRef(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const result = useQuery({
    queryKey: ['checkout-result', token],
    // Sent on every call rather than only the first; the server keeps track of which one was the
    // return, so later polls cost nothing extra.
    queryFn: () => checkoutApi.getResult(token, returnReference.current),
    enabled: token.length > 0,
    // A payment can still be settling when the payer returns, so poll briefly until it settles.
    refetchInterval: (query) => {
      const current = query.state.data;

      // Open, but its last attempt was refused: the payment is waiting for the payer, not for the
      // gateway. Polling would ask the same question for two minutes and answer it the same way.
      if (!isOpen(current?.status) || current?.lastAttemptFailed) {
        return false;
      }

      return elapsedRef.current < POLL_CEILING_MS ? 3_000 : false;
    },
  });

  /*
   * The payment is still payable and the payer's last attempt was declined. It is not a settled
   * outcome and not a wait either: the page has to read as a failure the payer can act on, which is
   * the one thing `status` alone cannot say.
   */
  const retryableFailure = Boolean(result.data?.lastAttemptFailed);

  const open = isOpen(result.data?.status) && !retryableFailure;
  const pollingStopped = open && elapsedMs >= POLL_CEILING_MS;

  /*
   * Bumped when the payer asks to check again after the wait has been given up on. Both clocks have
   * to restart together — the timer below retires itself at the ceiling, so resetting the elapsed
   * count without restarting the timer would leave the page polling every three seconds against a
   * clock that never advances again, which is the runaway the ceiling exists to prevent.
   */
  const [waitCycle, setWaitCycle] = useState(0);

  useEffect(() => {
    if (!open) {
      return;
    }

    // Date.now() belongs in the effect, not in the render: a clock read during render is not
    // idempotent, so the same render could produce a different answer each time React ran it.
    const started = Date.now() - elapsedRef.current;

    const timer = window.setInterval(() => {
      elapsedRef.current = Date.now() - started;
      setElapsedMs(elapsedRef.current);

      // Past the ceiling nothing further depends on the clock, so the timer retires itself rather
      // than ticking for as long as the tab stays open.
      if (elapsedRef.current >= POLL_CEILING_MS) {
        window.clearInterval(timer);
      }
    }, 1_000);

    return () => window.clearInterval(timer);
  }, [open, waitCycle]);

  const status = result.data?.status;
  const redirectUrl = result.data?.redirectUrl;

  // Only a settled payment returns the payer to the product, and only after a short pause so the
  // outcome is readable.
  const settled = status !== undefined && !isOpen(status);
  const willRedirect = Boolean(redirectUrl) && settled;

  useEffect(() => {
    if (!redirectUrl || !willRedirect) {
      return;
    }

    const timer = window.setTimeout(() => {
      window.location.href = redirectUrl;
    }, 4_000);

    return () => window.clearTimeout(timer);
  }, [redirectUrl, willRedirect]);

  if (result.isLoading) {
    return (
      <CheckoutCard>
        <div className="bp-spinner" role="status" aria-label={t('common.loading')} />
      </CheckoutCard>
    );
  }

  if (result.isError || !result.data) {
    // As on the payment page: a payment whose result could not be fetched is not the same as one that
    // does not exist, and the payer has already been charged by this point — so a connection failure
    // must read as "we could not check" and offer a retry, not as "this payment is gone".
    const unreachable = isRetryableApiError(result.error);

    return (
      <CheckoutCard>
        <div className="bp-result">
          <div className="bp-result__icon bp-result__icon--error" aria-hidden="true">
            <ResultGlyph kind="error" />
          </div>
          <div className="bp-result__title">
            {unreachable ? t('checkout.connectionLost') : t('checkout.notAvailable')}
          </div>
        </div>

        {unreachable ? (
          <button type="button" className="bp-button" onClick={() => void result.refetch()}>
            {t('checkout.tryAgain')}
          </button>
        ) : null}
      </CheckoutCard>
    );
  }

  /*
   * While the provider is still deciding, the page shows the verifying state rather than a "Waiting
   * for payment" card. The distinction matters to the payer: their money has already left, and a card
   * that reads as though nothing has happened is what prompts a second attempt.
   */
  if (open && !pollingStopped) {
    return (
      <CheckoutCard merchant={result.data.merchant}>
        <PaymentProgress phase="verifying" slow={elapsedMs >= SLOW_AFTER_MS} />
      </CheckoutCard>
    );
  }

  /*
   * Two minutes of asking and still nothing. The page has to say so.
   *
   * What stood here was the ordinary "payment pending" card, which is true and useless: it reads as
   * though the payment is progressing normally, and the payer's own reading of a page that has told
   * them nothing for two minutes is that it broke. So they pay again. One customer did it three
   * times, and each attempt left another payment nobody could account for — the failure this card
   * exists to stop.
   *
   * It therefore says the three things they actually need, in order: we cannot confirm it yet, do
   * not pay again, and here is how this ends. The "check again" button is the honest form of the
   * reload they were about to do anyway; it restarts the clock rather than the payment.
   */
  if (open && pollingStopped) {
    return (
      <CheckoutCard merchant={result.data.merchant}>
        <div className="bp-result bp-result--pending">
          <div className="bp-result__icon bp-result__icon--pending" aria-hidden="true">
            <ResultGlyph kind="pending" />
          </div>

          <div className="bp-result__title">{t('checkout.notConfirmed')}</div>
          <div className="bp-result__message">{t('checkout.doNotPayAgain')}</div>
          <div className="bp-result__message">{t('checkout.weWillFollowUp')}</div>
        </div>

        <div className="bp-receipt bp-receipt--pending">
          <dl className="bp-detail bp-detail--receipt">
            <dt>{t('payments.paymentNumber')}</dt>
            <dd>{result.data.paymentNumber}</dd>
          </dl>
        </div>

        <button
          type="button"
          className="bp-button"
          onClick={() => {
            elapsedRef.current = 0;
            setElapsedMs(0);
            setWaitCycle((cycle) => cycle + 1);
            void result.refetch();
          }}
        >
          {t('checkout.checkAgain')}
        </button>
      </CheckoutCard>
    );
  }

  const data = result.data;
  const presentation = retryableFailure ? DECLINED : describe(data.status);
  const title = t(presentation.titleKey);
  const apiMessage = isArabic ? data.messageAr : data.message;
  const merchantName = isArabic && data.merchant.nameAr ? data.merchant.nameAr : data.merchant.name;

  /*
   * The API's message often restates the outcome ("Payment successful."), which would read as the same
   * sentence twice under the heading. It is shown only when it adds something — a decline reason, say.
   */
  const message = apiMessage && normalise(apiMessage) !== normalise(title) ? apiMessage : null;

  return (
    <CheckoutCard merchant={data.merchant}>
      <div className={`bp-result bp-result--${presentation.tone}`}>
        <div className={`bp-result__icon bp-result__icon--${presentation.tone}`} aria-hidden="true">
          <ResultGlyph kind={presentation.tone} />
        </div>

        <div className="bp-result__title">{title}</div>

        {/* Localised, customer-safe text from the API — never a raw gateway message (spec 87). */}
        {message ? <div className="bp-result__message">{message}</div> : null}

        {/*
          Said plainly, because the payer's first question after a refusal is whether they have been
          charged, and their second is whether the link they were sent is now worthless.
        */}
        {retryableFailure ? <div className="bp-result__message">{t('checkout.noMoneyTaken')}</div> : null}
      </div>

      {/*
        The receipt: the amount in the outcome's colour, and the identifiers a payer would quote to
        support beneath it. The tone is repeated on the amount because it is the number the payer
        will remember, and "SAR 57.50 in green" and "SAR 57.50 in red" are different stories.
      */}
      <div className={`bp-receipt bp-receipt--${presentation.tone}`}>
        <div className="bp-receipt__amount">
          <div className="bp-receipt__label">{t('common.amount')}</div>
          <div className="bp-receipt__value">{formatMoney(data.amount, data.currency, i18n.language)}</div>
        </div>

        <dl className="bp-detail bp-detail--receipt">
          {data.referenceId ? (
            <>
              <dt>{t('checkout.reference')}</dt>
              <dd>{data.referenceId}</dd>
            </>
          ) : null}

          <dt>{t('payments.paymentNumber')}</dt>
          <dd>{data.paymentNumber}</dd>

          {data.completedAt ? (
            <>
              <dt>{t('checkout.completedAt')}</dt>
              <dd>{formatDateTime(data.completedAt, i18n.language)}</dd>
            </>
          ) : null}
        </dl>
      </div>

      {redirectUrl ? (
        <a className="bp-button" href={redirectUrl}>
          <span>{t('checkout.returnToMerchant', { merchant: merchantName })}</span>
          <ArrowIcon />
        </a>
      ) : null}

      {/* The auto-redirect is announced, so leaving the page a moment later is not a surprise. */}
      {willRedirect ? (
        <div className="bp-result__redirect" role="status">
          <span className="bp-result__redirect-bar" aria-hidden="true" />
          <span>{t('checkout.returningShortly', { merchant: merchantName })}</span>
        </div>
      ) : null}

      {data.status === 'Failed' || retryableFailure ? (
        <a
          className="bp-button bp-button--secondary"
          href={`/p/${encodeURIComponent(token)}`}
          style={{ marginBlockStart: 8 }}
        >
          <RetryIcon />
          <span>{t('checkout.tryAgain')}</span>
        </a>
      ) : null}

      {/*
        Automatic polling has given up, but the payment is still open. The payer keeps a way to ask
        again by hand rather than being left with a card that can no longer change — and a deliberate
        press costs the provider one status call, not one every three seconds.
      */}
      {pollingStopped ? (
        <button
          type="button"
          className="bp-button bp-button--secondary"
          style={{ marginBlockStart: 8 }}
          disabled={result.isFetching}
          onClick={() => void result.refetch()}
        >
          <RetryIcon />
          <span>{t('checkout.checkAgain')}</span>
        </button>
      ) : null}
    </CheckoutCard>
  );
}

type Tone = 'success' | 'error' | 'pending';

/** A refused attempt on a payment that is still open — a failure the payer can do something about. */
const DECLINED: { tone: Tone; titleKey: string } = { tone: 'error', titleKey: 'checkout.paymentFailed' };

function describe(status: string): { tone: Tone; titleKey: string } {
  switch (status) {
    case 'Succeeded':
    case 'Authorized':
      return { tone: 'success', titleKey: 'checkout.paymentSuccessful' };
    case 'Failed':
      return { tone: 'error', titleKey: 'checkout.paymentFailed' };
    case 'Cancelled':
      return { tone: 'error', titleKey: 'checkout.paymentCancelled' };
    case 'Expired':
      return { tone: 'error', titleKey: 'checkout.paymentExpired' };
    case 'UnderInvestigation':
      return { tone: 'pending', titleKey: 'checkout.underReview' };
    default:
      return { tone: 'pending', titleKey: 'checkout.paymentPending' };
  }
}

/** Trailing punctuation and case must not make two identical sentences look different. */
function normalise(value: string): string {
  return value.trim().replace(/[.!؟。]+$/u, '').toLocaleLowerCase();
}

/* Outcome glyphs drawn as strokes, so they scale cleanly and sit centred in the ring. */
function ResultGlyph({ kind }: { kind: Tone }) {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.6, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

  switch (kind) {
    case 'success':
      return (
        <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true" focusable="false">
          <path className="bp-result__stroke" d="M8 16.5l5.5 5.5L24 10.5" {...stroke} />
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true" focusable="false">
          <path className="bp-result__stroke" d="M10 10l12 12M22 10L10 22" {...stroke} />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true" focusable="false">
          <circle cx="16" cy="16" r="10.5" {...stroke} />
          <path d="M16 10v6.5l4 2.5" {...stroke} />
        </svg>
      );
  }
}

function ArrowIcon() {
  return (
    <svg className="bp-icon-flip" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RetryIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M13 8a5 5 0 1 1-1.6-3.7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M13 2.5V6H9.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
